import { MessageBar, MessageBarType, Stack, StackItem, Text, TextField } from '@fluentui/react';
import React, { useState } from 'react';
import {
  CaseAction,
  overturnCase,
  Case,
  QueueConfiguration,
  Opinion,
  OpinionCategoryLabels,
  Feedback,
  Action
} from 'services';
import { LabelSelection } from 'components/shared';
import { DefaultUxPreferences } from 'services';
import { generateModeratorFeedback } from '../util';
import { SalusPrimaryButton } from '../shared/SalusPrimaryButton';

const maxCommentLength = 500;

export interface ActionComponentProps {
  action: Action;
  undoTraits: { [actionId: string]: OpinionCategoryLabels };
  setUndoTraits: React.Dispatch<React.SetStateAction<{ [actionId: string]: OpinionCategoryLabels }>>;
}

export const ActionComponent: React.FunctionComponent<ActionComponentProps> = props => {
  return (
    <div key={props.action.action} className="mb-4">
      <div className="mb-3">
        <Text variant="xLarge">Undo {props.action.action}</Text>
      </div>
      {props.action.undoCategorizedTraits === undefined ||
      Object.keys(props.action.undoCategorizedTraits).length === 0 ? (
        <div>Cannot be undone.</div>
      ) : (
        <>
          <Text variant="mediumPlus">Traits</Text>
          <LabelSelection
            onComponentChange={(opinion: Opinion): void => {
              props.setUndoTraits({ ...props.undoTraits, [props.action.action]: opinion.opinionCategoryLabels });
            }}
            focusRef={React.createRef()}
            uxPreferences={DefaultUxPreferences}
            isDisabled={false}
            labelsData={props.action.undoCategorizedTraits}
          />
        </>
      )}
    </div>
  );
};

export interface OverturnFormProps {
  case: Case;
  config: QueueConfiguration;
  actions: CaseAction[];
  onSubmit: (feedback: Feedback) => void;
}

export const OverturnForm: React.FunctionComponent<OverturnFormProps> = props => {
  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [isPostPending, setPostPending] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [undoTraits, setUndoTraits] = useState<{ [actionId: string]: OpinionCategoryLabels }>({});

  const actions = props.actions.reduce(
    (prev, cur) =>
      prev.concat(
        cur.actions.map(entry => {
          entry.caseActionId = cur.id;
          return entry;
        })
      ),
    [] as Action[]
  );
  const reducedTraits = Object.keys(undoTraits).reduce((prev, cur) => {
    const actionTraits = undoTraits[cur];
    Object.keys(actionTraits).forEach(key => {
      if (prev[key] === undefined) {
        prev[key] = actionTraits[key];
      } else {
        prev[key] = [...new Set(prev[key].concat(actionTraits[key]))];
      }
    });
    return prev;
  }, {} as OpinionCategoryLabels);

  return (
    <Stack>
      <StackItem className="mb-4">
        <Text variant="xxLarge">Overturn ruling</Text>
        <br />
        <Text variant="medium">To undo actions, select a trait for each</Text>
      </StackItem>
      <StackItem>
        {actions.map(action => {
          return (
            <ActionComponent
              key={action.action}
              action={action}
              undoTraits={undoTraits}
              setUndoTraits={setUndoTraits}
            />
          );
        })}
      </StackItem>
      <StackItem>
        <Text variant="mediumPlus">Comments</Text>
        <TextField
          rows={5}
          type="textarea"
          maxLength={maxCommentLength}
          onChange={(event, value): void => {
            if (value !== undefined) {
              setComment(value);
            }
          }}
          multiline
          resizable={false}
          autoAdjustHeight
          placeholder={'Enter moderator notes (optional)'}
        />
        <Text variant="mediumPlus">
          {comment.length}/{maxCommentLength}
        </Text>
      </StackItem>
      {isApiError && (
        <StackItem className="mt-4">
          <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
            Failed to submit feedback.
          </MessageBar>
        </StackItem>
      )}
      <StackItem className="mt-4">
        <SalusPrimaryButton
          disabled={isPostPending || Object.keys(reducedTraits).length === 0}
          text="Apply"
          className="p-3"
          onClick={async (): Promise<void> => {
            setPostPending(true);
            const opinion = new Opinion(reducedTraits, comment);
            try {
              const result = await overturnCase(generateModeratorFeedback(props.case, opinion, props.config));
              props.onSubmit(result);
            } catch (error) {
              setIsApiError(true);
            } finally {
              setPostPending(false);
            }
          }}
        />
      </StackItem>
    </Stack>
  );
};
